import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import MasterService from '../../services/MasterService';
import './Vehicles.css';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_GETWAY_API+ "dif-auction/api/v1" || 'http://localhost:8626/dif-auction/api/v1';

const Vehicles = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [vehicles, setVehicles] = useState([]);
    const [tabs, setTabs] = useState([]);
    const [activeTab, setActiveTab] = useState(location.state?.tabId || '13'); // Default to "All" tab
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [mmvData, setMmvData] = useState({
        make: [],
        model: [],
        version: []
    });
    const [dealers, setDealers] = useState([]);
    const [searchData, setSearchData] = useState({
        search_text: '',
        dealer_id: '',
        make_id: '',
        model_id: '',
        variant_id: '',
        make_year: '',
        auction_status: '',
        created_by: '',
        created_start_date: null,
        created_end_date: null,
        reg_date: '',
        is_if_vehicle: ''
    });
    const [showIfVehicles, setShowIfVehicles] = useState(false);

    useEffect(() => {
        fetchMMVData();
        fetchDealers();
        fetchVehicleList(); 
    }, []);

    const handleDateRangeChange = (dates) => {
        const [start, end] = dates;
        setSearchData(prev => ({
            ...prev,
            created_start_date: start,
            created_end_date: end
        }));
    };

    useEffect(() => {
        fetchVehicleList(); 
    }, [activeTab, currentPage]);

    const fetchMMVData = async () => {
        try {
            const result = await MasterService.getMMV();
            
            if (result.data.status === 200) {
                setMmvData(result.data.data);
            }
        } catch (error) {
            console.error('Error fetching MMV data:', error);
        }
    };

    const fetchDealers = async () => {
        try {
            const result = await MasterService.getDealers();
            console.log("**************", result);
            if (result.status === 200) {
                const filteredDealers = result?.data?.data;
                setDealers(filteredDealers);
            }
        } catch (error) {
            console.error('Error fetching dealers:', error);
        }
    };

    const fetchVehicleListWithFilters = async (customRequestBody = null, ifVehicleOverride = null) => {
        setLoading(true);
        try {
            const formatDate = (date) => {
                if (!date) return null;
                if (typeof date === 'string') return date;
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            const requestBody = customRequestBody || {
                tab_id: activeTab,
                page_no: currentPage
            };

            // Add filter parameters only if they have values (when not using custom body)
            if (!customRequestBody) {
                if (searchData.search_text) requestBody.search_text = searchData.search_text;
                if (searchData.dealer_id) requestBody.dealer_id = parseInt(searchData.dealer_id);
                if (searchData.make_id) requestBody.make_id = parseInt(searchData.make_id);
                if (searchData.model_id) requestBody.model_id = parseInt(searchData.model_id);
                if (searchData.variant_id) requestBody.variant_id = parseInt(searchData.variant_id);
                if (searchData.make_year) requestBody.make_year = parseInt(searchData.make_year);
                if (searchData.auction_status) requestBody.auction_status = searchData.auction_status;
                if (searchData.created_by) requestBody.created_by = parseInt(searchData.created_by);
                if (searchData.created_start_date) {
                    requestBody.created_start_date = `${formatDate(searchData.created_start_date)} 00:00:00`;
                }
                if (searchData.created_end_date) {
                    requestBody.created_end_date = `${formatDate(searchData.created_end_date)} 23:59:59`;
                }
                if (searchData.reg_date) {
                    // Convert YYYY-MM-DD to MM/YYYY format
                    const [year, month] = searchData.reg_date.split('-');
                    requestBody.reg_date = `${month}/${year}`;
                }
                // Add IF vehicle filter - use override if provided, otherwise use state
                if (ifVehicleOverride !== null) {
                    if (ifVehicleOverride) {
                        requestBody.is_if_vehicle = '1';
                    }
                } else if (showIfVehicles) {
                    requestBody.is_if_vehicle = '1';
                }
            }

            console.log('Request body:', JSON.stringify(requestBody, null, 2));
            console.log('showIfVehicles state:', showIfVehicles);

            const response = await fetch(`${API_BASE_URL}/lead/vehicle_list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();
            
            if (result.status === 200) {
                const vehicleData = result.data.stocks.list.map(vehicle => ({
                    id: vehicle.lead_id,
                    dealerId: vehicle.dealer_id,
                    vehicleName: `${vehicle.make_name} ${vehicle.model_name} ${vehicle.variant_name}`,
                    vid: `VID${vehicle.lead_id}`,
                    regNo: vehicle.plate_number,
                    sellerName: vehicle.dealer_name, // You might need to get this from user data
                    location: vehicle.city_name,
                    qcStatus: vehicle.status,
                    statusColor: vehicle.status_color,
                    createdOn: vehicle.submission_date,
                    createdBy: vehicle.created_by_source === 'web' ? 'Web': 'Dealer',
                    makeYear: vehicle.make_year,
                    isFinalSubmission: vehicle.is_final_submission_done
                }));
                
                setVehicles(vehicleData);
                setTabs(result.data.stocks.tabs);
                setPagination(result.data.stocks.pagination);
            }
        } catch (error) {
            console.error('Error fetching vehicle list:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVehicleList = () => {
        fetchVehicleListWithFilters(); // Use current filters from state
    };

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        setCurrentPage(1); // Reset to first page when tab changes
    };

    const handlePageChange = (page) => {
        // Update current page state to trigger API call
        setCurrentPage(page);
    };

    const handleSearch = () => {
        setCurrentPage(1); // Reset to first page when searching
        fetchVehicleList(); // Fetch with current filters
    };

    const handleReset = () => {
        // Create the reset data object
        const resetData = {
            search_text: '',
            dealer_id: '',
            make_id: '',
            model_id: '',
            variant_id: '',
            make_year: '',
            auction_status: '',
            created_by: '',
            created_start_date: '',
            created_end_date: '',
            reg_date: ''
        };
        
        setSearchData(resetData);
        setCurrentPage(1); // Reset to first page
        
        // Use the reset data directly for the API call to avoid timing issues
        const requestBody = {
            tab_id: activeTab,
            page_no: 1
        };
        
        // No filters to add since we're resetting
        fetchVehicleListWithFilters(requestBody);
    };

    const handleEdit = (vehicle) => {
        console.log('Edit vehicle:', vehicle);
        // Navigate to edit page with lead_id and dealer_id parameters
        navigate(`/vehicles/add?lead_id=${vehicle.id}&dealer_id=${vehicle.dealerId}`);
    };

    const handleIfVehicleToggle = (checked) => {
        console.log('Toggle changed to:', checked);
        setShowIfVehicles(checked);
        setCurrentPage(1); // Reset to first page when toggle changes
        // Pass the new value directly to avoid state timing issues
        fetchVehicleListWithFilters(null, checked);
    };

    const handleAddVehicle = () => {
        navigate('/vehicles/add');
    };

    return (
        <div className="vehicles-container">
            {/* Header */}
            <header className="vehicles-header">
                <h1>Bank Auctions</h1>
                <div className="header-actions">
                    <button className=" btn-line" onClick={handleAddVehicle}>
                        <span className="btn-icon">Add New Vehicle</span>
                    </button>
                </div>
            </header>

            {/* Tabs Section */}
            <div className="tabs-section">
                <div className="tabs-container">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => handleTabClick(tab.id)}
                        >
                            {tab.label} ({tab.count})
                        </button>
                    ))}
                </div>
            </div>

            {/* Search and Filter Section */}
            <div className="search-filter-section">
                <div className="search-grid">
                    <div className="search-group">
                        <label>Search (VID, Reg No, Dealer ID)</label>
                        <input
                            type="text"
                            placeholder="Search by VID, Reg No, Dealer ID"
                            value={searchData.search_text}
                            onChange={(e) => setSearchData({...searchData, search_text: e.target.value})}
                        />
                    </div>
                    
                    <div className="search-group">
                        <label>Make</label>
                        <Select
                            value={searchData.make_id ? { value: searchData.make_id, label: mmvData.make?.find(m => m.id === parseInt(searchData.make_id))?.make } : null}
                            onChange={(selectedOption) => setSearchData({...searchData, make_id: selectedOption ? selectedOption.value : '', model_id: '', variant_id: ''})}
                            options={[
                                { value: '', label: 'All Makes' },
                                ...(mmvData.make?.map((make) => ({
                                    value: make.id,
                                    label: make.make
                                })) || [])
                            ]}
                            placeholder="All Makes"
                            isClearable
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="search-group">
                        <label>Model</label>
                        <Select
                            value={searchData.model_id ? { value: searchData.model_id, label: mmvData.model?.find(m => m.id === parseInt(searchData.model_id))?.m } : null}
                            onChange={(selectedOption) => setSearchData({...searchData, model_id: selectedOption ? selectedOption.value : '', variant_id: ''})}
                            options={[
                                { value: '', label: 'All Models' },
                                ...(mmvData.model?.filter(model => model.mk_id === parseInt(searchData.make_id)).map((model) => ({
                                    value: model.id,
                                    label: model.m
                                })) || [])
                            ]}
                            placeholder="All Models"
                            isClearable
                            isSearchable
                            isDisabled={!searchData.make_id}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="search-group">
                        <label>Variant</label>
                        <Select
                            value={searchData.variant_id ? { value: searchData.variant_id, label: mmvData.version?.find(v => v.vn_id === parseInt(searchData.variant_id))?.vn } : null}
                            onChange={(selectedOption) => setSearchData({...searchData, variant_id: selectedOption ? selectedOption.value : ''})}
                            options={[
                                { value: '', label: 'All Variants' },
                                ...(mmvData.version?.filter(variant => variant.md_id === parseInt(searchData.model_id)).map((variant) => ({
                                    value: variant.vn_id,
                                    label: variant.vn
                                })) || [])
                            ]}
                            placeholder="All Variants"
                            isClearable
                            isSearchable
                            isDisabled={!searchData.model_id}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="search-group">
                        <label>Year</label>
                        <Select
                            value={searchData.make_year ? { value: searchData.make_year, label: searchData.make_year } : null}
                            onChange={(selectedOption) => setSearchData({...searchData, make_year: selectedOption ? selectedOption.value : ''})}
                            options={[
                                { value: '', label: 'All Years' },
                                ...Array.from({length: new Date().getFullYear() - 1999}, (_, i) => new Date().getFullYear() - i).map(year => ({
                                    value: year,
                                    label: year.toString()
                                }))
                            ]}
                            placeholder="All Years"
                            isClearable
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="search-group">
                        <label>Auction Status</label>
                        <Select
                            value={searchData.auction_status ? { value: searchData.auction_status, label: tabs.slice(1).find(t => t.status === searchData.auction_status)?.label } : null}
                            onChange={(selectedOption) => setSearchData({...searchData, auction_status: selectedOption ? selectedOption.value : ''})}
                            options={[
                                { value: '', label: 'All Status' },
                                ...tabs.slice(1).map((tab) => ({
                                    value: tab.status,
                                    label: tab.label
                                }))
                            ]}
                            placeholder="All Status"
                            isClearable
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="search-group">
                        <label>Created By</label>
                        <Select
                            value={searchData.created_by ? { value: searchData.created_by, label: dealers?.find(d => d.id === parseInt(searchData.created_by))?.organization + ` (GCD${searchData.created_by})` } : null}
                            onChange={(selectedOption) => setSearchData({...searchData, created_by: selectedOption ? selectedOption.value : ''})}
                            options={[
                                { value: '', label: 'All Dealers' },
                                ...(dealers?.map((dealer) => ({
                                    value: dealer.id,
                                    label: `${dealer.organization} (GCD${dealer.id})`
                                })) || [])
                            ]}
                            placeholder="All Dealers"
                            isClearable
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="search-group">
                        <label>Created Date Range</label>
                        <DatePicker
                            selectsRange={true}
                            startDate={searchData.created_start_date ? new Date(searchData.created_start_date) : null}
                            endDate={searchData.created_end_date ? new Date(searchData.created_end_date) : null}
                            onChange={handleDateRangeChange}
                            placeholderText="Date Range"
                            autoComplete="off"
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            maxDate={new Date()}
                        />
                    </div>

                    <div className="search-group">
                        <label>Registration Date</label>
                        <DatePicker
                            selectsRange={false}
                            selected={searchData.reg_date ? new Date(searchData.reg_date) : null}
                            onChange={(date) => setSearchData({...searchData, reg_date: date ? date.toLocaleDateString('en-CA').split('T')[0] : ''})}
                            placeholderText="Select Month and Year"
                            autoComplete="off"
                            dateFormat="yyyy-MM"
                            className="form-control"
                            showMonthYearPicker
                            // maxDate={new Date()}
                        />
                    </div>
                </div>

                <div className="search-actions">
                    <button className="btn-primary" onClick={handleSearch}>
                        Search
                    </button>
                    <button className="btn-line" onClick={handleReset}>
                        Reset
                    </button>
                    <button 
                        onClick={() => handleIfVehicleToggle(!showIfVehicles)}
                       className='btn-line'
                    >
                        {showIfVehicles ? '✓ IF Vehicles ON' : 'Show IF Vehicles'}
                    </button>
                </div>
            </div>

            {/* Table Section */}
            <div className="table-section">
                <div className="table-container">
                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                        </div>
                    ) : (
                        <table className="vehicles-table">
                            <thead>
                                <tr>
                                    <th>Vehicle Name</th>
                                    <th>VID</th>
                                    <th>Reg. No.</th>
                                    <th>Seller Name</th>
                                    <th>Location</th>
                                    <th>QC Status</th>
                                    <th>Created On</th>
                                    <th>Created By</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.length > 0 ? (
                                    vehicles.map((vehicle) => (
                                        <tr key={vehicle.id}>
                                            <td>{vehicle.vehicleName}</td>
                                            <td>{vehicle.vid}</td>
                                            <td>{vehicle.regNo}</td>
                                            <td>{vehicle.sellerName}</td>
                                            <td>{vehicle.location}</td>
                                            <td>{vehicle.qcStatus}</td>
                                            <td>{vehicle.createdOn}</td>
                                            <td>{vehicle.createdBy}</td>
                                            <td>
                                                <button 
                                                    className="btn-line"
                                                    onClick={() => handleEdit(vehicle)}
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="no-data">
                                            No vehicles found for this category
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Pagination */}
            <div className="pagination-section">
                <div className="pagination-container">
                    <button 
                        className="pagination-btn" 
                        disabled={!pagination.hasPrev}
                        onClick={() => handlePageChange(currentPage - 1)}
                    >
                        Previous
                    </button>
                    <span className="page-info">
                        Page {currentPage} of {pagination.totalPages} 
                        ({pagination.total} total vehicles)
                    </span>
                    <button 
                        className="pagination-btn" 
                        disabled={!pagination.hasNext}
                        onClick={() => handlePageChange(currentPage + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Vehicles;
